import React, { useState } from "react";

interface AssessmentItem {
  id: string;
  label: string;
  score: number; // 1 = Independent, 2 = Some help, 3 = Extensive help, 4 = Total dependence / High risk
}

interface Domain {
  name: string;
  items: AssessmentItem[];
}

interface DependencyProfile {
  id: string;
  residentName: string;
  date: string;
  assessor: string;
  domains: Domain[];
  totalScore: number;
  maxScore: number;
  band: string;
  estimatedDailyMinutes: number;
  staffingRequirement: string;
  risks: string[];
  notes: string;
}

const KEY = "dependency_profiles";

const DEFAULT_DOMAINS: Domain[] = [
  {
    name: "Mobility & Transfers",
    items: [
      { id: "mob_bed", label: "Bed mobility & repositioning", score: 1 },
      { id: "mob_chair", label: "Chair / commode transfers", score: 1 },
      { id: "mob_walk", label: "Walking / mobility around home", score: 1 },
      { id: "mob_hoist", label: "Hoist / specialist equipment use", score: 1 },
    ],
  },
  {
    name: "Personal Care & Continence",
    items: [
      { id: "pc_wash", label: "Washing / bathing / showering", score: 1 },
      { id: "pc_dress", label: "Dressing / undressing", score: 1 },
      { id: "pc_toilet", label: "Toileting / pad changes", score: 1 },
      { id: "pc_cont", label: "Continence management (catheter/stoma)", score: 1 },
    ],
  },
  {
    name: "Nutrition & Hydration",
    items: [
      { id: "nut_eat", label: "Eating / cutting up food", score: 1 },
      { id: "nut_drink", label: "Drinking / fluid intake monitoring", score: 1 },
      { id: "nut_swallow", label: "Swallowing risks (dysphagia / thickened fluids)", score: 1 },
    ],
  },
  {
    name: "Clinical & Medication",
    items: [
      { id: "clin_meds", label: "Medication administration complexity", score: 1 },
      { id: "clin_wound", label: "Wound care / tissue viability", score: 1 },
      { id: "clin_devices", label: "Medical devices (PEG, Oxygen, CPAP)", score: 1 },
      { id: "clin_obs", label: "Vital signs / blood sugar monitoring", score: 1 },
    ],
  },
  {
    name: "Cognition, Behavior & Social",
    items: [
      { id: "cog_orient", label: "Orientation / dementia / confusion", score: 1 },
      { id: "cog_behav", label: "Challenging behavior / risk to self or others", score: 1 },
      { id: "cog_1to1", label: "Requires 1-to-1 supervision / engagement", score: 1 },
    ],
  },
];

const loadProfiles = (): DependencyProfile[] => {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
};
const loadResidents = (): any[] => {
  try { return JSON.parse(localStorage.getItem("residents_records") || "[]"); } catch { return []; }
};
const today = () => new Date().toISOString().slice(0, 10);

const calculateOutputs = (domains: Domain[]) => {
  const totalScore = domains.reduce((sum, d) => sum + d.items.reduce((s, i) => s + i.score, 0), 0);
  const maxScore = domains.reduce((sum, d) => sum + d.items.length * 4, 0);
  
  // Rough estimation: Base 60 mins + (Score * 4 minutes per point)
  const estimatedDailyMinutes = 60 + (totalScore * 4);
  
  let band = "Low";
  let staffingRequirement = "Standard group care";
  
  if (totalScore >= 60) { band = "Very High / 1-to-1"; staffingRequirement = "Dedicated 1-to-1 staff or 2:1 for all transfers"; }
  else if (totalScore >= 45) { band = "High"; staffingRequirement = "2 staff for all personal care and transfers"; }
  else if (totalScore >= 30) { band = "Medium"; staffingRequirement = "1 staff + 1 assist for heavy transfers"; }
  
  const risks: string[] = [];
  domains.forEach(d => d.items.forEach(i => {
    if (i.score >= 3) {
      if (i.id.includes("mob") || i.id.includes("hoist")) risks.push("High Falls / Manual Handling Risk");
      if (i.id.includes("nut_swallow")) risks.push("Choking / Aspiration Risk");
      if (i.id.includes("pc_cont")) risks.push("Infection Control / Skin Integrity Risk");
      if (i.id.includes("cog_behav")) risks.push("Behavioral / Safety Risk");
      if (i.id.includes("clin_wound")) risks.push("Tissue Viability Risk");
    }
  }));

  return { totalScore, maxScore, estimatedDailyMinutes, band, staffingRequirement, risks: Array.from(new Set(risks)) };
};

export default function DependencyTool({ user }: { user?: any }) {
  const residents = loadResidents();
  const [profiles, setProfiles] = useState<DependencyProfile[]>(loadProfiles);
  const [residentName, setResidentName] = useState("");
  const [domains, setDomains] = useState<Domain[]>(JSON.parse(JSON.stringify(DEFAULT_DOMAINS)));
  const [notes, setNotes] = useState("");
  const [message, setMessage] = useState("");

  const saveProfiles = (next: DependencyProfile[]) => {
    setProfiles(next);
    localStorage.setItem(KEY, JSON.stringify(next));
  };

  const updateScore = (domainIdx: number, itemIdx: number, score: number) => {
    const next = JSON.parse(JSON.stringify(domains));
    next[domainIdx].items[itemIdx].score = score;
    setDomains(next);
  };

  const saveAssessment = () => {
    if (!residentName) { setMessage("Select a resident."); return; }
    const outputs = calculateOutputs(domains);
    const profile: DependencyProfile = {
      id: `dep-${Date.now()}`,
      residentName,
      date: today(),
      assessor: user && user.name ? user.name : "",
      domains,
      notes,
      ...outputs,
    };
    saveProfiles([profile, ...profiles]);
    setMessage(`Dependency profile saved for ${residentName}: ${profile.band} band, ~${profile.estimatedDailyMinutes} mins care/day.`);
    setDomains(JSON.parse(JSON.stringify(DEFAULT_DOMAINS)));
    setNotes("");
  };

  const printJustification = (p: DependencyProfile) => {
    const rows = p.domains.map(d => `
      <tr><td colspan="3" style="background:#123B36;color:white;padding:6px;font-weight:bold">${d.name}</td></tr>
      ${d.items.map(i => `<tr><td style="padding:4px 6px;border-bottom:1px solid #eee">${i.label}</td><td style="padding:4px 6px;border-bottom:1px solid #eee;text-align:center">${i.score}/4</td><td style="padding:4px 6px;border-bottom:1px solid #eee">${i.score === 1 ? "Independent" : i.score === 2 ? "Some help" : i.score === 3 ? "Extensive help" : "Total dependence"}</td></tr>`).join("")}
    `).join("");
    
    const html = `<!DOCTYPE html><html><head><title>Care Needs Justification</title><style>body{font-family:Arial;margin:40px;color:#1f2937}h2{color:#123B36}table{width:100%;border-collapse:collapse;font-size:13px}.summary{background:#f3f4f6;padding:15px;border-radius:8px;margin-bottom:20px}</style></head><body>
      <h2>Care Needs & Dependency Justification Report</h2>
      <p><strong>Resident:</strong> ${p.residentName} | <strong>Assessed:</strong> ${p.date} by ${p.assessor}</p>
      <div class="summary">
        <h3>Executive Summary</h3>
        <p><strong>Dependency Band:</strong> ${p.band} (Score: ${p.totalScore} / ${p.maxScore})</p>
        <p><strong>Estimated Staff Time Required:</strong> ${p.estimatedDailyMinutes} minutes per day</p>
        <p><strong>Staffing Requirement:</strong> ${p.staffingRequirement}</p>
        <p><strong>Identified Risks:</strong> ${p.risks.length ? p.risks.join(", ") : "Standard risks only"}</p>
      </div>
      <h3>Detailed Assessment Matrix</h3>
      <table><tr><th>Activity / Need</th><th>Score</th><th>Level of Support</th></tr>${rows}</table>
      <h3>Assessor Notes & Justification</h3>
      <p>${p.notes || "No additional notes."}</p>
      <p style="margin-top:30px;font-size:11px;color:#6b7280">This document provides the evidenced, clinical justification for the resident's care plan, staffing allocation, and funding band, in compliance with CQC Regulation 12 (Safe Care and Treatment) and Regulation 9 (Person-Centred Care).</p>
    </body></html>`;
    const win = window.open("", "_blank");
    if (win) { win.document.write(html); win.document.close(); win.focus(); win.print(); }
  };

  const outputs = calculateOutputs(domains);

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Full Dependency & Care Needs Tool</h2>
        <p className="text-sm text-slate-600">Comprehensive ADL and clinical assessment. Calculates exact staff minutes, dependency band, and generates a CQC-compliant justification report.</p>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 font-semibold text-slate-900">New Dependency Assessment</h3>
        <div className="mb-4 flex flex-wrap gap-3">
          <select value={residentName} onChange={e => setResidentName(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
            <option value="">Select Resident</option>
            {residents.map((r: any) => <option key={r.id} value={r.name}>{r.name}</option>)}
          </select>
          <div className={`rounded-lg px-3 py-1 text-sm font-semibold ${outputs.band.includes("Very High") ? "bg-red-100 text-red-700" : outputs.band === "High" ? "bg-amber-100 text-amber-700" : outputs.band === "Medium" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>
            {outputs.band} | {outputs.totalScore}/{outputs.maxScore} pts | ~{outputs.estimatedDailyMinutes} mins/day
          </div>
        </div>

        <div className="space-y-4">
          {domains.map((domain, dIdx) => (
            <div key={domain.name} className="rounded-lg border border-slate-200 p-3">
              <h4 className="mb-2 font-semibold text-slate-800">{domain.name}</h4>
              <div className="grid gap-2 md:grid-cols-2">
                {domain.items.map((item, iIdx) => (
                  <div key={item.id} className="flex items-center justify-between rounded border border-slate-100 p-2">
                    <span className="text-sm text-slate-700">{item.label}</span>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4].map(s => (
                        <button key={s} onClick={() => updateScore(dIdx, iIdx, s)} className={`w-8 h-8 rounded text-xs font-semibold ${item.score === s ? (s === 4 ? "bg-red-600 text-white" : s === 3 ? "bg-amber-500 text-white" : s === 2 ? "bg-blue-500 text-white" : "bg-green-500 text-white") : "bg-slate-100 text-slate-600"}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {outputs.risks.length > 0 ? (
          <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm font-semibold text-red-700">
            High Risks Identified: {outputs.risks.join(" | ")}
          </div>
        ) : null}

        <div className="mt-4">
          <label className="mb-1 block text-xs font-medium text-slate-600">Assessor Notes & Justification</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Detail why this resident requires this level of care, specific equipment needed, etc." className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
        </div>

        <div className="mt-3 flex gap-2">
          <span className="text-sm text-slate-600 self-center">Staffing Requirement: <strong>{outputs.staffingRequirement}</strong></span>
          <button onClick={saveAssessment} className="ml-auto rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Save & Lock Assessment</button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 font-semibold text-slate-900">Saved Dependency Profiles (Audit Trail)</h3>
        <div className="space-y-2">
          {profiles.map(p => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 p-3">
              <div>
                <div className="text-sm font-medium text-slate-900">{p.residentName} <span className="text-xs text-slate-500">Assessed {p.date} by {p.assessor}</span></div>
                <div className="text-xs text-slate-600">{p.band} | {p.totalScore}/{p.maxScore} pts | {p.estimatedDailyMinutes} mins/day | {p.staffingRequirement}</div>
              </div>
              <button onClick={() => printJustification(p)} className="rounded-lg bg-blue-900 px-3 py-1 text-xs text-white">Print CQC Justification</button>
            </div>
          ))}
          {profiles.length === 0 ? <div className="text-sm text-slate-500">No profiles saved yet.</div> : null}
        </div>
      </div>
    </div>
  );
}